"use client";

import {
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  User,
} from "firebase/auth";

import { logLogin, logSignup, setAnalyticsUserId } from "@/lib/analytics";
import { auth } from "@/lib/firebase";
import { usernameFromEmail } from "@/lib/utils";
import {
  createUserProfile,
  getCurrentUser as getFirestoreUser,
} from "@/lib/users";

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();
// Force account selection on every sign-in
googleProvider.setCustomParameters({
  prompt: "select_account",
});

/**
 * Set authentication cookie for middleware
 */
const setAuthCookie = async () => {
  try {
    const user = auth.currentUser;

    if (!user) return;

    const idToken = await user.getIdToken();

    await fetch("/api/auth/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken }),
    });
  } catch (error) {
    console.error("Error setting auth cookie:", error);
  }
};

/**
 * Clear authentication cookie
 */
const clearAuthCookie = async () => {
  try {
    await fetch("/api/auth/session", {
      method: "DELETE",
    });
  } catch (error) {
    console.error("Error clearing auth cookie:", error);
  }
};

/**
 * Sign in with Google
 */
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);

    // Set auth cookie for middleware
    await setAuthCookie();

    // Log analytics event
    logLogin("google");
    if (result.user.uid) {
      setAnalyticsUserId(result.user.uid);
    }

    return { user: result.user, error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";

    console.error("Error signing in with Google:", error);

    return { user: null, error: errorMessage };
  }
};

/**
 * Sign out
 */
export const signOut = async () => {
  try {
    // Clear auth cookie first
    await clearAuthCookie();
    await firebaseSignOut(auth);

    return { error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";

    console.error("Error signing out:", error);

    return { error: errorMessage };
  }
};

/**
 * Subscribe to auth state changes
 */
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Sign up with email and password
 */
export const signUpWithEmail = async (
  email: string,
  password: string,
  name: string,
) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);

    // Create user profile in Firestore
    await createUserProfile(
      result.user.uid,
      result.user.email || email,
      name ||
        result.user.displayName ||
        usernameFromEmail(result.user.email || email) ||
        "User",
      result.user.photoURL || "",
    );

    const userData = await getFirestoreUser();

    // Set auth cookie for middleware
    await setAuthCookie();

    // Log analytics event
    logSignup("email");
    logLogin("email");

    if (result.user.uid) {
      setAnalyticsUserId(result.user.uid);
    }

    return { user: userData, error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";

    console.error("Error signing up with email:", error);

    return { user: null, error: errorMessage };
  }
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (email: string, password: string) => {
  try {
    await signInWithEmailAndPassword(auth, email, password);
    const userData = await getFirestoreUser();

    // Set auth cookie for middleware
    await setAuthCookie();

    // Log analytics event
    logLogin("email");
    if (userData && userData.id) {
      setAnalyticsUserId(userData.id);
    }

    return { user: userData, error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";

    console.error("Error signing in with email:", error);

    return { user: null, error: errorMessage };
  }
};

/**
 * Send password reset email
 */
export const sendPasswordReset = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);

    return { error: null };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";

    console.error("Error sending password reset email:", error);

    return { error: errorMessage };
  }
};
